import { useRef, useState, useEffect } from 'react';
import { useInView, motion, AnimatePresence } from 'motion/react';
import { Box, Stack, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SectionLayout from './SectionLayout';
import turtleRace1 from '../images/turtle-race/1.jpg';
import turtleRace2 from '../images/turtle-race/2.jpg';
import turtleRace3 from '../images/turtle-race/3.jpg';
import turtleRace4 from '../images/turtle-race/4.jpg';
import turtleRace5 from '../images/turtle-race/5.jpg';
import turtleRace6 from '../images/turtle-race/6.jpg';
import pianoGame1 from '../images/piano-game/1.jpg';
import pianoGame2 from '../images/piano-game/2.jpg';
import pianoGame3 from '../images/piano-game/3.jpg';
import pianoGame4 from '../images/piano-game/4.jpg';
import pianoGame5 from '../images/piano-game/5.jpg';
import ripples1 from '../images/kinetic-ripple/1.jpg';
import ripples2 from '../images/kinetic-ripple/2.jpg';
import ripples3 from '../images/kinetic-ripple/3.jpg';
import plainSight1 from '../images/plain-sight/1.jpg';
import plainSight2 from '../images/plain-sight/2.jpg';
import plainSight3 from '../images/plain-sight/3.jpg';


const turtleRaceImages = [
  turtleRace1,
  turtleRace2,
  turtleRace3,
  turtleRace4,
  turtleRace5,
  turtleRace6,
];

const pianoGameImages = [
  pianoGame1,
  pianoGame2,
  pianoGame3,
  pianoGame4,
  pianoGame5
];

const ripplesImages = [
  ripples1,
  ripples2,
  ripples3
];

const plainSightImages = [
  plainSight1,
  plainSight2,
  plainSight3,
];

const TABS = [
  { id: 'hardware', label: 'Physical Builds' },
  { id: 'software', label: 'Software' },
];

// Shown in a tab that has no projects yet.
const comingSoon = {
  id: 'coming-soon',
  title: 'Coming soon',
  shortDescription: 'A project is on the way',
  details: "I'm currently building something to feature here — check back soon.",
};

const projects = [
  {
    id: 'turtle-race',
    category: 'hardware',
    title: 'Turtle Race',
    shortDescription: 'A two-player electromechanical racing game',
    details:
      'Each player controls a turtle with a single button. A stoplight on the far side of the road governs when you can move — press on green to advance, but pressing on red sends your turtle backwards. The turtles sit on top of the board and are magnetically coupled to a pulley-driven mechanism underneath.',
    images: turtleRaceImages,
  },
  {
    id: 'rhythm-piano',
    category: 'hardware',
    title: 'Rhythm Piano',
    shortDescription: 'A 4-key rhythm game inspired by Guitar Hero',
    details:
      'A physical piano with 4 keys and an LED matrix display above them. Lines fall from the top of the matrix toward each key and you have to press the matching key at the right moment. Built with custom electronics and embedded firmware.',
    images: pianoGameImages,
    background: 'rgba(80, 12, 16, 0.55)',
  },
  {
    id: 'ripple',
    category: 'hardware',
    title: 'Ripple',
    shortDescription: 'A kinetic sculpture that mimics ripples on water',
    details:
      'Concentric rings that each rise and fall independently, choreographed so their motion propagates outward like a ripple spreading across the surface of water. The entire mechanism is driven by one motor.',
    images: ripplesImages,
    background: 'rgba(12, 28, 72, 0.6)',
  },
  {
    id: 'plain-sight',
    category: 'software',
    title: 'Plain Sight',
    shortDescription: 'A steganography tool that hides one image inside another',
    details:
      "A browser-based steganography demo: it tucks a secret image into the least-significant bits of a cover image, so the result looks untouched. Drag to shift the bits and reveal what's hidden in plain sight.",
    images: plainSightImages,
    link: '/plain-sight/',
  },
];

// The longest details copy, used as an invisible sizer so the details block
// always reserves the height of the tallest project and never reflows.
const longestDetails = projects.reduce(
  (longest, p) => (p.details.length > longest.length ? p.details : longest),
  '',
);

function ImageSlideshow({ images, alt }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return undefined;
    const id = setInterval(() => {
      setCurrent((i) => (i + 1) % images.length);
    }, 2800);
    return () => clearInterval(id);
  }, [images.length]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <AnimatePresence>
        <motion.div
          key={current}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          style={{ position: 'absolute', inset: 0 }}
        >
          <Box
            component="img"
            src={images[current]}
            alt={alt}
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </motion.div>
      </AnimatePresence>
    </Box>
  );
}

function ProjectCard({ project }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.25)',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.05) 45%, rgba(255,255,255,0.09) 100%)',
        backdropFilter: 'blur(22px) saturate(180%)',
        WebkitBackdropFilter: 'blur(22px) saturate(180%)',
        boxShadow:
          '0 10px 40px rgba(0,0,0,0.35), inset 0 1px 1px rgba(255,255,255,0.4), inset 0 -1px 1px rgba(255,255,255,0.08)',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: { xs: 150, sm: 320 },
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.3)',
          fontSize: '14px',
          overflow: 'hidden',
          filter: 'brightness(0.9) saturate(1.05)',
        }}
      >
        {project.images?.length ? (
          <ImageSlideshow images={project.images} alt={project.title} />
        ) : project.image ? (
          <Box
            component="img"
            src={project.image}
            alt={project.title}
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          'Photo coming soon'
        )}
      </Box>
      <Box sx={{ p: 2 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '20px' }}>{project.title}</h3>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.4, opacity: 0.7, minHeight: '2.8em' }}>
          {project.shortDescription}
        </p>
        {/* The details sit in a grid with an invisible sizer holding the
            longest copy, so every card is the same height and the carousel
            doesn't shift as you page through. */}
        <Box sx={{ display: 'grid', mt: 1 }}>
          <p
            aria-hidden
            style={{ gridArea: '1 / 1', margin: 0, fontSize: 'clamp(12px, 3.3vw, 14px)', lineHeight: 1.5, visibility: 'hidden' }}
          >
            {longestDetails}
          </p>
          <p style={{ gridArea: '1 / 1', margin: 0, fontSize: 'clamp(12px, 3.3vw, 14px)', lineHeight: 1.5, opacity: 0.85 }}>
            {project.details}
          </p>
        </Box>
        {project.link && (
          <Box
            component="a"
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              position: 'relative',
              zIndex: 2,
              display: 'inline-block',
              mt: 2,
              fontSize: 13,
              letterSpacing: '1px',
              color: 'white',
              textDecoration: 'none',
              border: '1px solid rgba(255,255,255,0.6)',
              borderRadius: 2,
              px: 2,
              py: 0.75,
              transition: 'background-color 0.2s',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
            }}
          >
            Open project →
          </Box>
        )}
      </Box>

      {/* A single glass pane laid over the whole card — a diagonal specular
          sheen plus a soft top highlight — so the image and text read as one
          sheet of frosted glass rather than a photo with glassy edges. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.06) 28%, rgba(255,255,255,0) 55%, rgba(255,255,255,0.05) 100%)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)',
          mixBlendMode: 'screen',
        }}
      />
    </Box>
  );
}

function ProjectsSection({ thirdRef, onScrollUp, onScrollDown, onProjectChange, style }) {
  const inViewRef = useRef(null);
  const inView = useInView(inViewRef, { amount: 0.3 });
  const [activeTab, setActiveTab] = useState('hardware');
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const setRefs = (el) => {
    thirdRef.current = el;
    inViewRef.current = el;
  };

  const visibleProjects = projects.filter((p) => p.category === activeTab);
  const hasProjects = visibleProjects.length > 0;
  const multiple = visibleProjects.length > 1;

  useEffect(() => {
    if (!inView) {
      onProjectChange?.(null);
      return;
    }
    const list = projects.filter((p) => p.category === activeTab);
    onProjectChange?.(list[index]?.id ?? null);
  }, [inView, index, activeTab, onProjectChange]);

  const selectTab = (id) => {
    if (id === activeTab) return;
    setActiveTab(id);
    setDirection(1);
    setIndex(0);
  };

  const prev = () => {
    if (!multiple) return;
    setDirection(-1);
    setIndex((i) => (i - 1 + visibleProjects.length) % visibleProjects.length);
  };

  const next = () => {
    if (!multiple) return;
    setDirection(1);
    setIndex((i) => (i + 1) % visibleProjects.length);
  };

  return (
    <SectionLayout
      sectionRef={setRefs}
      style={style}
      onScrollUp={onScrollUp}
      onScrollDown={onScrollDown}
      arrowsVisible={inView}
    >
      {/* Pillarbox: black bars framing an 1100px center stage on wide screens. */}
      <Box
        aria-hidden
        sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 'calc(50% + 550px)',
          backgroundColor: 'black',
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden
        sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          left: 'calc(50% + 550px)',
          backgroundColor: 'black',
          pointerEvents: 'none',
        }}
      />
      <Stack spacing={{ xs: 2, sm: 3 }} alignItems="center" sx={{ maxWidth: '760px', px: 2 }}>
        <h2 style={{ fontSize: 'clamp(24px, 7vw, 36px)', margin: 0, whiteSpace: 'nowrap', textShadow: '0 0 18px rgba(255,255,255,0.45)' }}>Personal Projects</h2>

        <Box sx={{ display: 'flex', gap: 4 }}>
          {TABS.map((t) => (
            <Box
              key={t.id}
              onClick={() => selectTab(t.id)}
              sx={{
                cursor: 'pointer',
                fontSize: 14,
                letterSpacing: '1px',
                color: 'white',
                opacity: activeTab === t.id ? 1 : 0.45,
                borderBottom: '2px solid',
                borderColor: activeTab === t.id ? 'white' : 'transparent',
                pb: '4px',
                transition: 'opacity 0.2s, border-color 0.2s',
                userSelect: 'none',
              }}
            >
              {t.label}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {multiple && (
            <IconButton onClick={prev} sx={{ color: 'inherit' }}>
              <ArrowBackIosNewIcon />
            </IconButton>
          )}

          <Box sx={{ width: 'min(520px, calc(100vw - 130px))', overflow: 'hidden' }}>
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={`${activeTab}-${index}`}
                custom={direction}
                initial={{ x: direction * 80, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: direction * -80, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ProjectCard project={hasProjects ? visibleProjects[index] : comingSoon} />
              </motion.div>
            </AnimatePresence>
          </Box>

          {multiple && (
            <IconButton onClick={next} sx={{ color: 'inherit' }}>
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </Box>

        {multiple && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {visibleProjects.map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: i === index ? 'white' : 'rgba(255,255,255,0.3)',
                  transition: 'background-color 0.3s',
                }}
              />
            ))}
          </Box>
        )}
      </Stack>
    </SectionLayout>
  );
}

export default ProjectsSection;
